/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { SWATCH } from '@/data/constants/catalog';
import { ContentContext } from '@/data/context/content';
import { ProductAttribute, Selection } from '@/data/types/Product';
import { getAttributeType } from '@/utils/productAttributes';
import {
	FormControl,
	FormControlLabel,
	// Radio,
	RadioGroup,
	Stack,
	Button,
	styled,
	useTheme,
	useMediaQuery,
	Box,
	Skeleton,
} from '@mui/material';
import { isEqual } from 'lodash';
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useProductDetails } from '@/data/Content/ProductDetails';

const tallaOrder = {
	ech: 1,
	ch: 2,
	m: 3,
	g: 4,
	xg: 5,
};

type WithFormControlConditionalProps = {
	isSwatch: boolean;
	children: JSX.Element | JSX.Element[];
	onAttributeChange: (attrId: string, valueId: string) => void;
	attrIdentifier: string;
	value: any;
	handleChange: (event: any) => void;
};
const WithFormControlConditional: FC<WithFormControlConditionalProps> = ({
	isSwatch,
	onAttributeChange,
	attrIdentifier,
	children,
	handleChange,
}) => (
	<Stack spacing={1} direction={isSwatch ? 'row' : 'row'}>
		{isSwatch ? (
			children
		) : (
			<FormControl component="fieldset">
				<RadioGroup
					sx={{
						flexDirection: 'row',
						label: {
							marginRight: '0px',
							marginLeft: '0px',
						},
					}}
					onChange={(_e, value) => onAttributeChange(attrIdentifier, value)}
					onClick={handleChange}
				>
					{children}
				</RadioGroup>
			</FormControl>
		)}
	</Stack>
);

const StyledButton = styled(Button)`
	${({}) => `
    /* Estilo personalizado para el estado 'disabled' */
		&.Mui-disabled {
			opacity: 1;
			cursor: pointer !important;
		}
	font-weight: normal;
	border: 1px solid #C9C9C9;
    background: #FFFFFF 0% 0% no-repeat padding-box;
    color: #2C2C2C !important;
	cursor: pointer !important;
	border-radius: 0px;
	min-width: 68px;
	max-height: 43px;
`}
`;

type ProductAttributeV2Props = {
	attribute: ProductAttribute;
	skusAsAttrsList: Record<string, string>[];
	onAttributeChange: (attrId: string, valueId: string) => void;
	selection: Selection;
	isDrawer?: boolean;
	hasStock: (stock: boolean) => void;
	isPivot?: boolean;
};
export const ProductAttributeV2: FC<ProductAttributeV2Props> = ({
	attribute,
	skusAsAttrsList,
	onAttributeChange,
	selection,
	isPivot = false,
}) => {
	const { availability, sortedIdsWithSize, isLoadingInventory } = useContext(
		ContentContext
	) as ReturnType<typeof useProductDetails>;

	const { values, identifier: attrIdentifier } = attribute;
	const { attrsByIdentifier: currentlySelected = {} } = selection;
	const isSwatch = SWATCH === getAttributeType(values);
	const valueId = isSwatch ? currentlySelected[attrIdentifier] : null;

	const [value, setValue] = useState(valueId);
	const [active, setActive] = useState<string>('');
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [isStock, setIsStock] = useState<boolean>();

	const handleChange = (event: any) => {
		setValue(event.target.value);
		onAttributeChange(attrIdentifier, event.target.value);
	};

	const handleButtonClick = (optionValue: string) => {
		setActive(optionValue);
	};

	const tallas = attribute['attribute.natural'];
	const identifierTalla: string[] = [];
	const valueTalla: string[] = [];

	values.forEach(({ identifier, value }) => {
		identifierTalla.push(identifier);
		valueTalla.push(value);
	});
	const allTallas = JSON.parse(tallas);

	allTallas.sort();
	identifierTalla.sort();
	valueTalla.sort();

	const initialAvailability = useRef(availability);

	useEffect(() => {
		if (!initialAvailability.current && availability) {
			initialAvailability.current = availability;
		}
		if (initialAvailability.current) {
			const stock = initialAvailability.current?.find((avial) => avial.status === true);
			setIsStock(!!stock);
		}
	}, [availability]);

	useEffect(() => {
		if (!initialAvailability.current && availability) {
			initialAvailability.current = availability;
		}
	}, [availability]);

	const sortByTalla = useCallback((tallaA: string, tallaB: string) => {
		const tallaALower = tallaA.toLowerCase();
		const tallaBLower = tallaB.toLowerCase();

		if (
			tallaOrder[tallaALower as keyof typeof tallaOrder] !== undefined &&
			tallaOrder[tallaBLower as keyof typeof tallaOrder] !== undefined
		) {
			return (
				tallaOrder[tallaALower as keyof typeof tallaOrder] -
				tallaOrder[tallaBLower as keyof typeof tallaOrder]
			);
		}

		const numA = parseFloat(tallaA);
		const numB = parseFloat(tallaB);

		if (!isNaN(numA) && !isNaN(numB)) {
			return numA - numB;
		}

		if (tallaOrder[tallaALower as keyof typeof tallaOrder] !== undefined) {
			return -1;
		}
		if (tallaOrder[tallaBLower as keyof typeof tallaOrder] !== undefined) {
			return 1;
		}

		return 0;
	}, []);

	const newAvailability = useMemo(
		() =>
			sortedIdsWithSize
				?.sort((a, b) => sortByTalla(a.talla as string, b.talla as string))
				?.map((item) => {
					const availabilityItem = availability?.find((avail) => avail.productId === item.id);
					return {
						...item,
						...availabilityItem,
					};
				}),
		[availability, sortedIdsWithSize, sortByTalla]
	);

	// Si no hay talla seleccionada y solo hay una talla, se selecciona la primera talla
	useEffect(() => {
		if (!selection?.attrsByIdentifier?.Talla && newAvailability?.length === 1) {
			const talla = newAvailability[0].talla?.toString();
			if (talla) {
				setValue(talla);
				setActive(talla);
				onAttributeChange(attrIdentifier, talla);
			}
		}
	}, [selection, newAvailability, attrIdentifier, onAttributeChange]);

	return (
		<Stack spacing={1}>
			{!initialAvailability || isLoadingInventory || !availability || isStock === undefined ? (
				<Box
					sx={
						isMobile
							? {
									display: 'flex',
									overflowX: 'auto',
									whiteSpace: 'wrap',
									width: '100%',
									padding: '8px 0',
									'&::-webkit-scrollbar': {
										display: 'none',
									},
							  }
							: { display: 'flex', flexWrap: 'wrap' }
					}
				>
					{Array.from({ length: 6 }).map((_, index) => (
						<Skeleton
							key={index}
							variant="rectangular"
							sx={{
								marginRight: '2px',
								marginBottom: '2px',
								width: isMobile ? '68px' : '63px',
								height: '43px',
							}}
						/>
					))}
				</Box>
			) : (
				<WithFormControlConditional
					{...{ isSwatch, onAttributeChange, attrIdentifier, value, handleChange }}
				>
					{isMobile ? (
						<Box
							sx={{
								display: 'flex',
								overflowX: 'auto',
								whiteSpace: 'wrap',
								width: '100%',
								padding: '8px 0',
								'&::-webkit-scrollbar': {
									display: 'none',
								},
							}}
						>
							{newAvailability?.map((talla: any, index: number) => {
								const selected = currentlySelected[attrIdentifier] === identifierTalla[index];
								const asSku = selected
									? null
									: { ...currentlySelected, [attrIdentifier]: identifierTalla[index] };
								const disabled =
									!selected && !skusAsAttrsList.find((sku) => isEqual(sku, asSku)) && !isPivot;

								if (talla.inventoryStatus === 'Unavailable') {
									return null;
								}

								if (isStock) {
									return (
										<FormControlLabel
											key={talla.partNumber}
											value={
												talla.talla.charAt(0).toUpperCase() + talla.talla.slice(1).toLowerCase()
											}
											control={
												<StyledButton
													sx={
														active === talla.talla
															? {
																	backgroundColor: '#E8F3FF',
																	color: '#011688',
																	border: '1px solid #011688',
																	borderRadius: '2px 0px 0px 0px',
															  }
															: {}
													}
													onClick={() => handleButtonClick(talla.talla)}
												>
													{talla.talla}
												</StyledButton>
											}
											checked={selected}
											disabled={disabled}
											label={''}
											sx={{
												'&.Mui-disabled': {
													cursor: 'pointer !important',
												},
											}}
										/>
									);
								}
								return (
									<Box
										key={talla.partNumber}
										sx={{
											padding: '9px 16px',
											display: 'grid',
											placeItems: 'center',
											fontSize: '15px',
											border: '1px solid #C9C9C9',
											cursor: 'pointer !important',
											position: 'relative',
										}}
									>
										<Box
											sx={{
												position: 'absolute',
												width: '74px',
												height: '1px',
												backgroundColor: '#C9C9C9',
												transform: 'rotate(34deg)',
											}}
										></Box>
										{talla.talla}
									</Box>
								);
							})}
						</Box>
					) : (
						<Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
							{newAvailability?.map((talla: any, index: number) => {
								const selected = currentlySelected[attrIdentifier] === identifierTalla[index];
								const asSku = selected
									? null
									: { ...currentlySelected, [attrIdentifier]: identifierTalla[index] };
								const disabled =
									!selected && !skusAsAttrsList.find((sku) => isEqual(sku, asSku)) && !isPivot;

								if (talla.inventoryStatus === 'Unavailable') {
									return null;
								}

								if (isStock) {
									return (
										<FormControlLabel
											key={talla.partNumber}
											value={
												talla.talla.charAt(0).toUpperCase() + talla.talla.slice(1).toLowerCase()
											}
											control={
												<StyledButton
													sx={
														active === talla.talla
															? {
																	backgroundColor: '#E8F3FF',
																	color: '#011688',
																	border: '1px solid #011688',
																	borderRadius: '2px 0px 0px 0px',
															  }
															: { cursor: 'pointer !important' }
													}
													onClick={() => handleButtonClick(talla.talla)}
												>
													{talla.talla}
												</StyledButton>
											}
											checked={selected}
											disabled={disabled}
											label={''}
											sx={{
												'&.Mui-disabled': {
													cursor: 'pointer !important',
												},
											}}
										/>
									);
								}

								return (
									<Box
										key={talla.partNumber}
										sx={{
											padding: '9px 16px',
											width: '68px',
											display: 'grid',
											placeItems: 'center',
											fontSize: '15px',
											border: '1px solid #C9C9C9',
											cursor: 'pointer !important',
											position: 'relative',
										}}
									>
										<Box
											sx={{
												position: 'absolute',
												width: '77px',
												height: '1px',
												backgroundColor: '#C9C9C9',
												transform: 'rotate(31.5deg)',
											}}
										></Box>
										{talla.talla}
									</Box>
								);
							})}
						</Box>
					)}
				</WithFormControlConditional>
			)}
		</Stack>
	);
};
